import { useEffect, useMemo } from 'react'

const CHAT_READ_KEY_PREFIX = 'chat-read-'

export function getChatReadKey(quoteId: string): string {
  return `${CHAT_READ_KEY_PREFIX}${quoteId}`
}

export function setChatReadTimestamp(quoteId: string): void {
  localStorage.setItem(getChatReadKey(quoteId), String(Date.now()))
}

/**
 * Filters unread messages from an ES query against a localStorage timestamp
 * set when the user last read messages. This handles ES indexing lag where
 * readMessages updates CouchDB but ES hasn't re-indexed yet.
 *
 * Messages with timestamps newer than the last-read time pass through
 * (they arrived after the user read), so new messages still trigger notifications.
 *
 * The localStorage entry is cleared once ES catches up (returns 0 unread).
 * If ES never catches up (e.g. indexing failure), the entry persists — this is
 * intentional: suppressing already-read messages is correct, and new messages
 * always have newer timestamps so they still show.
 */
export function useChatReadFilter<T extends { details?: { timestamp?: number } }>(
  quoteId: string,
  unreadMessages: T[]
): T[] {
  const chatReadKey = useMemo(() => getChatReadKey(quoteId), [quoteId])
  const lastReadTime = parseInt(localStorage.getItem(chatReadKey) || '0', 10)

  // Clear localStorage once ES has caught up
  useEffect(() => {
    if (lastReadTime > 0 && unreadMessages.length === 0) {
      localStorage.removeItem(chatReadKey)
    }
  }, [unreadMessages, lastReadTime, chatReadKey])

  if (lastReadTime <= 0) return unreadMessages

  return unreadMessages.filter((msg) => (msg.details?.timestamp ?? 0) > lastReadTime)
}
